import {Component, Input, OnInit} from "@angular/core";
import {TrackingEvent} from "../../entities";

@Component({
    selector: "app-timeline",
    templateUrl: "./timeline.component.html",
    styleUrls: ["./timeline.component.scss"],
    standalone: false
})
export class TimelineComponent implements OnInit {

  @Input()
  events: TrackingEvent[] = [];

  /**
   * The index within `this.events` of the last event of status 'actual'
   */
  public lastActualEventIndex = 0;


  constructor() {
  }

  ngOnInit(): void {
    for (let i = this.events.length - 1; i >= 0; i--) {
      if (this.events[i].eventClassifierCode === "ACT") {
        this.lastActualEventIndex = i;
        break;
      }
    }
  }


  /**
   * Returns the timeline icon which should be used for this event
   */
  computeIcon(event: TrackingEvent) {
    if (event.equipmentEventTypeCode === "GTIN") return "gate-in";
    if (event.equipmentEventTypeCode === "GTOT") return "gate-out";
    if (event.eventType === "EQUIPMENT") return "shipping-container";
    if (event.eventType === "SHIPMENT") return "icon-view-document";
    return "freighter";
  }


  /**
   * Returns true if the event should be greyed out
   */
  isMuted(index: number) {
    return index > this.lastActualEventIndex;
  }


  /**
   * Returns true if any visible (core) event exists before the given index.
   * Used to decide whether the upper connector line should be drawn, since
   * non-core events are hidden and must not be treated as the first event.
   */
  hasCoreEventBefore(index: number): boolean {
    return this.events.slice(0, index).some(event => event.coreEvent);
  }


  /**
   * Returns true if any visible (core) event exists after the given index.
   * Used to decide whether the lower connector line should be drawn.
   */
  hasCoreEventAfter(index: number): boolean {
    return this.events.slice(index + 1).some(event => event.coreEvent);
  }


  /*
   * Show-more feature disabled: non-core events are no longer displayed.
   * Restore these two methods (and the related markup in
   * timeline.component.html) to bring back the "Show N more events" reveal.
   *
   * // Reveal the non-core event at the given index, plus any non-core events following it
   * revealEvents(index: number) {
   *   this.events[index].coreEvent = true;
   *
   *   for (let i = index + 1; i < this.events.length; i++) {
   *     if (!this.events[i].coreEvent) this.events[i].coreEvent = true;
   *     else break;
   *   }
   * }
   *
   * // Counts the number of non-core events which follow the element at the given index.
   * // The given index is also included in the count.
   * countHiddenEvents(index: number) {
   *   let total = 0;
   *   for (let i = index; i < this.events.length; i++) {
   *     if (!this.events[i].coreEvent) total++;
   *     else break;
   *   }
   *
   *   return total;
   * }
   */


  /*
   * Carriers spread location data across different places depending on the
   * event: some put a full address (city/country) on the event's own
   * location, others only carry a facility name there and keep city/country
   * on the transport call, and some provide just a locationName. Each field
   * below is therefore resolved independently across all sources, rather
   * than picking a single address object and losing whichever fields it
   * happens to be missing.
   */

  /**
   * Returns the city to display for this event, falling back to the plain
   * location name when no source provides a city.
   */
  getEventCity(event: TrackingEvent): string | undefined {
    return event.eventLocation?.address?.city
      || event.transportCall?.location?.address?.city
      || event.eventLocation?.locationName
      || event.transportCall?.location?.locationName;
  }


  /**
   * Returns the country to display for this event.
   */
  getEventCountry(event: TrackingEvent): string | undefined {
    return event.eventLocation?.address?.country
      || event.transportCall?.location?.address?.country;
  }


  /**
   * Returns the facility/site name (depot, terminal, customer location...)
   * when the carrier provides one and it isn't just the city repeated.
   */
  getEventFacilityName(event: TrackingEvent): string | undefined {
    const name = event.eventLocation?.address?.name || event.eventLocation?.locationName;
    if (!name) return undefined;

    const city = this.getEventCity(event);
    if (city && name.trim().toLowerCase() === city.trim().toLowerCase()) return undefined;
    return name;
  }


  /**
   * Returns the UN/LOCODE associated with this event, if one is available:
   * the event's own location first, then the transport call's location,
   * then the transport call itself.
   */
  getEventUNLocationCode(event: TrackingEvent): string | undefined {
    return event.eventLocation?.UNLocationCode
      || event.transportCall?.location?.UNLocationCode
      || event.transportCall?.UNLocationCode;
  }


  /**
   * Returns true when no location detail at all is available for the event.
   */
  hasNoLocationInfo(event: TrackingEvent): boolean {
    return !this.getEventCity(event)
      && !this.getEventCountry(event)
      && !this.getEventFacilityName(event)
      && !this.getEventUNLocationCode(event);
  }


  /**
   * Removes the timezone information from an ISO date/time string in order to return the 'local time'
   */
  getLocalTime(dateTime?: string) {
    if (!dateTime) return "";
    if (!dateTime.includes("+")) return dateTime;
    return dateTime.substring(0, dateTime.indexOf("+"));
  }
}
